import Vue from 'vue'
import Vuex from 'vuex'
import moment from "moment";

import { LocalStorage } from '@/resources/localStorage';
import { Core } from "@/core/Core";
import BranchActions from "./actions/branchActions";
import ReportActions from "./actions/reportActions";
import CompanyActions from "./actions/companyActions";
import ExternalPosActions from "./actions/externalPosActions";
import InventoryActions from "./actions/inventoryActions";
import LoginActions from "./actions/loginActions";
import LoyaltyActions from "./actions/loyaltyActions";
import PromotionActions from "./actions/promotionActions";
import TaxActions from "./actions/taxActions";
import UserActions from "./actions/userActions";
import PromotionModalActions from "./actions/promotionModalActions";
import ApiKeyActions from "./actions/apiKeyActions";
import PosActions from "./actions/posActions";


Vue.use(Vuex);



const store = new Vuex.Store({

  state: {
    pageTitleTranslationKey: "dashboard.title",
    pageIcon: "",
    currentPageSubtitle: "Super Admin",
    currentPageActions: [],
    locale: "fr",
    userName: "Laurent Ipsum",
    showSpinner: false,
    sales: [],
    loggedUser: {
      id: null,
      token: null,
      isSuperAdmin: false,
      isDistributor: false,
      isBranchAdmin: false,
      isCompanyAdmin: false,
      isClerkUser: false,
      isReceptionUser: false,
      isPosUser: false,
      info: null,
      branch: null,
      company: null,
      permission: null,
      homeServer: null,
      accessibleServers: null,
      accessibleBranches: null,
      user: {
        email: null,
        firstName: null,
        lastName: null
      }
    },
    contextServer: null,
    contextBranch: null,
    contextBranchLoading: false,
    contextCompany: null,
    contextCompanyLoading: false,
    contextInventory: null,
    contextInventoryLoading: false,
    contextVersion: null,
    versionChangeCounter: 0, /* hack-around to reload StagingSelect upcoming versions */
    breadcrumbs: [],
    pageTitle: null,
    headerMenuVisible: null,

    modalProps: {},
    modalComponent: null,
    modalComponentProps: null,
  },

  getters: {
    loggedUserHasSuperAdmin: (state) => state.loggedUser.isSuperAdmin,
    loggedUserHasDistributor: (state, getters) => state.loggedUser.isDistributor || getters.loggedUserHasSuperAdmin,
    loggedUserHasBranchAdmin: (state, getters) => state.loggedUser.isBranchAdmin || getters.loggedUserHasDistributor,
    loggedUserHasCompanyAdmin: (state, getters) => state.loggedUser.isCompanyAdmin || getters.loggedUserHasBranchAdmin,
    loggedUserHasClerkUser: (state, getters) => state.loggedUser.isReceptionUser || state.loggedUser.isClerkUser || getters.loggedUserHasCompanyAdmin,
    loggedUserHasPosUser: (state) => state.loggedUser.isPosUser,
    getContextInventory: async (state) => {
      while (state.contextBranchLoading || state.contextCompanyLoading || state.contextInventoryLoading) {
        await wait(100);
      }
      return Promise.resolve(state.contextInventory);
    },
    isBranchContextInventory: (state) => {
      if (state.contextBranchLoading || state.contextCompanyLoading || state.contextInventoryLoading || !state.contextBranch || !state.contextCompany) {
        return false;
      }
      return !!state.contextBranch.inventories.find(i => i.id === state.contextCompany.inventory.id);
    },
    getOverloadableFields: (state) => {
      if (state.contextBranchLoading) {
        return [];
      }
      return state.contextBranch.overloadableFields;
    },
    loggedUserCanSaveInventory: (state, getters) => {
      if (getters.isBranchContextInventory && getters.getOverloadableFields.length > 0
        && (!state.loggedUser.isDistributor || (state.loggedUser.info && state.loggedUser.info.canModify))) {
        return true;
      }
      return getters.loggedUserCanModifyInventory;
    },
    loggedUserCanModifyInventory: (state, getters) => {
      if (getters.isBranchContextInventory) {
        return false;
      }
      return getters.loggedUserHasModifyPermission;
    },
    loggedUserHasModifyPermission: (state) => {
      if (!state.loggedUser.isDistributor) {
        return true;
      }
      return state.loggedUser.info && state.loggedUser.info.canModify;
    },
    loggedUserHasViewPaymentSettingsPermission: (state, getters) => {
      if (!getters.loggedUserHasDistributor) {
        return false;
      }
      if (getters.loggedUserHasSuperAdmin) {
        return true;
      }
      return state.loggedUser.info && state.loggedUser.info.canViewPaymentSettings;
    },
    loggedUserHasEditPaymentSettingsPermission: (state, getters) => {
      if (!getters.loggedUserHasDistributor) {
        return false;
      }
      if (getters.loggedUserHasSuperAdmin) {
        return true;
      }
      return state.loggedUser.info && state.loggedUser.info.canEditPaymentSettings;
    },
    getContextCompany: async (state) => {
      while (state.contextBranchLoading || state.contextCompanyLoading) {
        await wait(100);
      }
      return Promise.resolve(state.contextCompany);
    },
    getContextBranch: async (state) => {
      while (state.contextBranchLoading) {
        await wait(100);
      }
      return Promise.resolve(state.contextBranch)
    },
    getContextLanguages: async (state, getters) => {
      let inventory = await getters.getContextInventory;
      let company = await getters.getContextCompany;
      if (inventory && company) {
        let inventoryLanguages = inventory.languages || [];
        return [company.locale].concat(inventoryLanguages.filter(l => l !== company.locale))
      }
    },
    getContextCurrency: async (state, getter) => {
      let inventory = await getter.getContextInventory;
      return inventory ? inventory.currency : null
    },
    getContextCurrencySymbol: async (state, getter) => {
      let currency = await getter.getContextCurrency;
      let locale = state.locale;

      switch (currency) {
        case "GBP":
          return "£";
        case "EUR":
          return "€";
        case "CAD":
          return locale === "en" ? "CA$" : "$CA";
        case "USD":
          return "US$";
        default :
          return "$";
      }
    },
    getContextCompanyActivatedModule: async (state, getters) => {
      let company = await getters.getContextCompany;
      if (!company) {
        return {}
      }
      return {
        onlineOrdering: company.onlineOrdering.activated,
        kiosk: company.kiosk.activated,
        menuBoard: company.menuBoard.activated,
        catering: false,
        payAtTheTable: company.payAtTheTable.activated,
        POS: company.activePOS.includes("ishop"),
        uberEats: !!company.uberEats && company.uberEats.store_id
      }
    },
    getContextBranchActivatedModule: async (state, getters) => {
      let branch = await getters.getContextBranch;
      if (!branch) {
        return {}
      }
      return {
        onlineOrdering: branch.onlineOrdering.activated,
        kiosk: branch.kiosk.activated,
        mobileApp: branch.mobileApp.activated,
        menuBoard: branch.menuBoard.activated,
        loyalty: branch.loyaltyActivated || branch.eWalletActivated,
        thirdPartyLoyalty: branch.dataCandyActivated || branch.freeBeesActivated,
        catering: branch.onlineOrdering.catering ? branch.onlineOrdering.catering.activated : false,
        onSiteOrdering : branch.onSiteOrdering,
        payAtTheTable: branch.payAtTheTable.activated,
        aimsVouchers: branch.aimsVouchersActivated,
        dispatch: branch.dispatch
      }
    },
    defaultRouting: (state) => {
      let user = state.loggedUser;
      let branch = user.branch || undefined;
      let company = user.company || undefined;

      if (user.isSuperAdmin) {
        return { path: '/branches' };
      } else if (user.isDistributor) {
        return { path: '/user-branches'};
      } else if (user.isBranchAdmin) {
        return { path: '/companies', query: {branch}};
      } else if (user.isCompanyAdmin) {
        return { path: '/categories', query: {branch, company}};
      } else if (user.isClerkUser) {
        return { path: '/quick-config', query: {branch, company}};
      } else if (user.isReceptionUser) {
        return { path: '/reception', query: {branch, company} };
      } else if (user.isPosUser) {
        return { path: '/pos', query: {branch, company}};
      } else {
        return null;
      }
    },
    isTokenExpired: () => {
      let expiry = LocalStorage.read('login-expiry');
      return !expiry || moment().isAfter(expiry)
    },
    isHeaderVisible: state => {
      return route => {
        if (state.headerMenuVisible !== null) {
          return state.headerMenuVisible;
        }
        return route.name !== "login" && route.name !== "resetPassword" && route.name !== "Training";
      };
    },
    isMenuVisible: state => {
      return route => {
        if (state.headerMenuVisible !== null) {
          return state.headerMenuVisible;
        }
        return route.name !== "login" && route.name !== "resetPassword" && route.name !== "Training";
      };
    },
  },

  mutations: {

    showSpinner(state, show) {
      state.showSpinner = show;
    },

    currentPageActions(state, actions) {
      state.currentPageActions = actions;
    },

    tokenExpiration(state) {
      let user = state.loggedUser;
      let hours = 1;
      if (user && (user.isSuperAdmin || user.isDistributor || user.isCompanyAdmin || user.isBranchAdmin)) {
        hours = 15;
      } else if (user && (user.isPosUser || user.isClerkUser || user.isReceptionUser)) {
        hours = 8760;
      }
      LocalStorage.write("login-expiry", moment().add({hours}).format());
    },

    setContextServer(state, server) {
      LocalStorage.write("context-server", server);
      Core.setContextServer(server);
      state.contextServer = server;
    },

    setContextVersion(state, version) {
      LocalStorage.write("context-version", version);
      state.contextVersion = version;
    },

    notifyVersionChange(state) {
      state.versionChangeCounter++
    },

    logIn(state, user) {
      LocalStorage.write("user", user);
      Core.setLoginToken(user.token);
      state.loggedUser = user;
    },

    setUser(state, user) {
      LocalStorage.write("user", user);
      state.loggedUser = user;
    },

    logOut(state) {
      LocalStorage.write("user", null);
      LocalStorage.write("login-expiry", null);
      LocalStorage.write("context-server", null);
      Core.setLoginToken(null);
      Core.setContextServer(null);
      state.contextServer = null;
      state.loggedUser = {
        id: null,
        token: null,
        isSuperAdmin: false,
        isDistributor: false,
        isBranchAdmin: false,
        isCompanyAdmin: false,
        isClerkUser: false,
        isReceptionUser: false,
        isPosUser: false,
        branch: null,
        info: null,
        company: null,
        permission: null,
        homeServer: null,
        accessibleServers: null,
        accessibleBranches: null
      }
    },

    async setContext(state, {branchId, companyId, hardRefresh}) {
      branchId = parseInt(branchId) || null;
      companyId = parseInt(companyId) || null;
      let branchLoadFailed = false;
      let branchChanged = branchId !== (state.contextBranch ? state.contextBranch.id : null);
      let companyChanged = companyId !== (state.contextCompany ? state.contextCompany.id : null);

      if (!branchId) {
        state.contextBranch = null;
      }
      else if (branchId && branchChanged) {
        try {
          state.contextBranchLoading = true;
          let branchResponse = await Core.request("GET", `/api/branches/${branchId}?augment=backend`);
          state.contextBranch = branchResponse;
          state.contextBranchLoading = false;
          /* keep inventory on refresh */
          if (!companyId) {
            let inventory = null;
            if (hardRefresh) {
              let savedInventoryId = parseInt(localStorage.getItem("context-inventory"));
              if (savedInventoryId) {
                inventory = state.contextBranch.inventories.find(i => i.id === savedInventoryId);
              }
            }
            if (!inventory) {
              inventory = branchResponse.inventories[0] || null;
            }
            state.contextInventory = inventory;
          }
        } catch (e) {
          console.log("Could not load branch", e);
          branchLoadFailed = true;
          if (typeof nw !== "undefined") {
            console.log("app will reload in 30 seconds...");
            setTimeout(() => {
              window.location.reload();
            }, 30 * 1000);
          }
        }
      }

      if (branchLoadFailed) {
        return;
      }

      if (!companyId) {
        state.contextCompany = null;
      }
      else if (companyId && companyChanged) {
        try {
          state.contextCompanyLoading = true;
          state.contextInventoryLoading = true;
          let companyResponse = await Core.request("GET", `/api/branches/${branchId}/companies/${companyId}?augment=backend`);
          state.contextCompany = companyResponse;
          state.contextInventory = companyResponse.inventory;
          state.contextCompanyLoading = false;
          state.contextInventoryLoading = false;
        } catch (e) {
          console.log("Could not load company", e);
          if (typeof nw !== "undefined") {
            console.log("app will reload in 30 seconds...");
            setTimeout(() => {
              window.location.reload();
            }, 30 * 1000);
          }
        }
      }

      /* Case when backing from company and context inventory doesn't belong to that Branch */
      if (branchId && !branchChanged && !companyId && companyChanged) {
        let contextInventoryId = state.contextInventory ? state.contextInventory.id : null;
        if (state.contextBranch && !state.contextBranch.inventories.find(inv => inv.id === contextInventoryId)) {
          state.contextInventory = state.contextBranch.inventories[0] || null;
        }
      }
    },

    updateContextBranch(state, branch) {
      state.contextBranch = branch;
    },

    updateContextInventory(state, inventory) {
      state.contextInventory = inventory;
    },

    setHeaderMenuVisible(state, visible) {
      state.headerMenuVisible = visible;
    }
  },

  actions: {
    ...BranchActions,
    ...ReportActions,
    ...CompanyActions,
    ...ExternalPosActions,
    ...InventoryActions,
    ...LoginActions,
    ...PromotionActions,
    ...TaxActions,
    ...UserActions,
    ...LoyaltyActions,
    ...PromotionModalActions,
    ...ApiKeyActions,
    ...PosActions
  },

});

export default store;
